import { useEffect, useMemo, useRef, useState } from "react";
import type { Destination } from "@/app/destinations";

/**
 * Per the handoff the rail budgets 44 px for a nav button and 17 px for a separator,
 * and collapses whatever no longer fits into a "More" popover.
 */
export const NAV_BUTTON_HEIGHT = 44;
export const SEPARATOR_HEIGHT = 17;
export const MORE_BUTTON_HEIGHT = 44;

export interface RailEntry {
  kind: "destination" | "separator";
  destination?: Destination;
  key: string;
}

export interface RailSplit {
  /** What the rail draws, in order, with trailing separators already removed. */
  visible: RailEntry[];
  /** Destinations that moved into the More popover, in their original order. */
  hidden: Destination[];
}

export function toRailEntries(destinations: Destination[]): RailEntry[] {
  const entries: RailEntry[] = [];

  for (const destination of destinations) {
    entries.push({ kind: "destination", destination, key: destination.id });
    if (destination.separatorAfter) entries.push({ kind: "separator", key: `sep-${destination.id}` });
  }

  return entries;
}

function heightOf(entry: RailEntry): number {
  return entry.kind === "separator" ? SEPARATOR_HEIGHT : NAV_BUTTON_HEIGHT;
}

/** A separator is only meaningful between two things, so trailing ones are dropped. */
function dropTrailingSeparators(entries: RailEntry[]): RailEntry[] {
  let end = entries.length;
  while (end > 0 && entries[end - 1]!.kind === "separator") end -= 1;
  return entries.slice(0, end);
}

/**
 * Decides what fits in `availableHeight`.
 *
 * Two passes, because the answer changes its own question: the "More" button only
 * exists if something overflows, but it occupies space that might be what caused the
 * overflow. So measure once assuming everything fits, and only if it does not, measure
 * again with room reserved for the button.
 */
export function splitRail(entries: RailEntry[], availableHeight: number): RailSplit {
  if (!Number.isFinite(availableHeight) || availableHeight <= 0) {
    return { visible: dropTrailingSeparators(entries), hidden: [] };
  }

  const total = entries.reduce((sum, entry) => sum + heightOf(entry), 0);
  if (total <= availableHeight) {
    return { visible: dropTrailingSeparators(entries), hidden: [] };
  }

  const budget = availableHeight - MORE_BUTTON_HEIGHT;
  const visible: RailEntry[] = [];
  const hidden: Destination[] = [];
  let used = 0;
  let overflowing = false;

  for (const entry of entries) {
    const next = used + heightOf(entry);

    // Once anything has overflowed, everything after it follows into the popover —
    // otherwise the rail would reorder itself, which is worse than truncating.
    if (overflowing || next > budget) {
      overflowing = true;
      if (entry.kind === "destination" && entry.destination) hidden.push(entry.destination);
      continue;
    }

    visible.push(entry);
    used = next;
  }

  return { visible: dropTrailingSeparators(visible), hidden };
}

/**
 * Measures the rail's own nav area and splits it. A real measurement rather than a
 * breakpoint, because what fits depends on how many destinations exist — which grows
 * with the milestones — not on how wide the window is.
 */
export function useRailOverflow(destinations: Destination[]) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [availableHeight, setAvailableHeight] = useState(Number.POSITIVE_INFINITY);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    // Older browsers, and jsdom, have no ResizeObserver. Falling back to "everything
    // fits" degrades to the pre-overflow behaviour rather than hiding destinations.
    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(([entry]) => {
      if (entry) setAvailableHeight(entry.contentRect.height);
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const entries = useMemo(() => toRailEntries(destinations), [destinations]);
  const split = useMemo(() => splitRail(entries, availableHeight), [entries, availableHeight]);

  return { containerRef, ...split };
}
